import { useCallback } from 'react';
import {
  LabelSuggestionDetails,
  LabelType,
  TimeSignal,
  TimeSignalClassification,
  usePutLabelSuggestionsMutation
} from 'graphql/types';
import { useMeContext } from 'modules/me/MeContextProvider';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks';
import { useFeatureFlag } from 'modules/common/hooks';
import { useNotesToastContext } from '../../NotesToastContextProvider';
import { clearCachedDates } from '../../cache/utils/time-signal-utils';
import { mapDateToISOString } from '../../../../utils/date-time';
import { TimesheetLabel } from '../../../timesheet-labels/labelSchema';
import {
  GetUpdateTimeSignalInput,
  useUpdateTimeSignalsById
} from '../../hooks/useUpdateTimeSignalsById';

type OnMultiEditTimeSignalsInput = {
  notes: TimeSignal[];
  updates: {
    classificationTarget: TimeSignalClassification;
    labelValues: TimesheetLabel[];
  };
};

interface UpdateFederatedSuggestionsInput {
  userUri: string | undefined;
  notes: TimeSignal[];
  labelsToUpdate: TimesheetLabel[];
  putSuggestions: any;
  hasCPTBeenUpdated: boolean;
}

const CPT_LABELS = [
  LabelType.Client,
  LabelType.Project,
  LabelType.Program,
  LabelType.Task,
  LabelType.Billing
];

const isCPTUpdated = (labelValues: TimesheetLabel[]) => {
  return labelValues.some(
    (lv: TimesheetLabel) => lv.value && CPT_LABELS.includes(lv.type)
  );
};

const getLabelsToUpdate = (
  labelValues: TimesheetLabel[],
  hasCPTBeenUpdated: boolean
) => {
  return labelValues.filter((lv: TimesheetLabel) => {
    return (
      lv.value || (hasCPTBeenUpdated ? CPT_LABELS.includes(lv.type) : false)
    );
  });
};

const updateFederatedSuggestions = async ({
  userUri,
  notes,
  labelsToUpdate,
  putSuggestions,
  hasCPTBeenUpdated
}: UpdateFederatedSuggestionsInput) => {
  const labelsToUpdateIds = labelsToUpdate.map((lv: TimesheetLabel) => lv.id);
  const updateSuggestionPromises = notes.map(async (note: TimeSignal) => {
    const federatedSuggestionsToKeep = note.suggestions.suggestions.filter(
      (suggestion: LabelSuggestionDetails) => {
        return (
          !labelsToUpdateIds.includes(suggestion.label.id) &&
          (hasCPTBeenUpdated
            ? !CPT_LABELS.includes(suggestion.label.type)
            : true)
        );
      }
    );
    if (note?.id && userUri && note?.labelValuesInferenceId) {
      await putSuggestions({
        variables: {
          input: {
            userUri: userUri,
            inferenceId: note.labelValuesInferenceId,
            timeSignalId: note.id,
            startTime: mapDateToISOString(note.startTime),
            endTime: mapDateToISOString(note.endTime),
            duration: note.duration ?? null,
            suggestions: federatedSuggestionsToKeep.map(
              (suggestion: LabelSuggestionDetails) =>
                ({
                  labelId: suggestion.label.id,
                  suggestionMetadata: suggestion.suggestionMetadata ?? null,
                  thresholdMet: suggestion.thresholdMet ?? false,
                  suggestedValues:
                    suggestion?.suggestedValues?.map((sv) => ({
                      idValue: sv?.idValue,
                      stringValue: sv?.stringValue,
                      numericValue: sv?.numericValue
                    })) ?? []
                } as any)
            )
          }
        }
      });
    }
  });
  return Promise.all(updateSuggestionPromises);
};

export const useMultiEditTimeSignals = (): {
  onMultiEditTimeSignals: (args: OnMultiEditTimeSignalsInput) => Promise<void>;
  updateInProgress: boolean;
} => {
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const isEmptyLabelsEnabled = useFeatureFlag('isEmptyLabelsEnabled', false);

  const [
    putSuggestions,
    { loading: putSuggestionsLoading, error: putSuggestionsError }
  ] = usePutLabelSuggestionsMutation();
  const { updateTimeSignalsById, loading, error } = useUpdateTimeSignalsById();
  const { setToastContext } = useNotesToastContext();
  const { id: userUri } = useMeContext() ?? {};
  const onMultiEditTimeSignals = useCallback(
    async ({ notes, updates }: OnMultiEditTimeSignalsInput) => {
      clearCachedDates(userUri);

      const hasCPTBeenUpdated = isCPTUpdated(updates.labelValues);
      const labelsToUpdate = getLabelsToUpdate(
        updates.labelValues,
        hasCPTBeenUpdated
      );
      const newTimeSignals = notes.map((note: TimeSignal) =>
        GetUpdateTimeSignalInput({
          timeSignal: note,
          updates: {
            classificationUpdate: updates.classificationTarget,
            labelValues: labelsToUpdate
          },
          flags: {
            isSmartSuggestionEnabled: isSmartSuggestionEnabled,
            isTimeSignalFederatedSuggestionsEnabled: isTimeSignalFederatedSuggestionsEnabled,
            isEmptyLabelsEnabled: isEmptyLabelsEnabled
          }
        })
      );

      await Promise.all([
        updateTimeSignalsById({
          updates: newTimeSignals
        }),
        isTimeSignalFederatedSuggestionsEnabled
          ? updateFederatedSuggestions({
              userUri: userUri,
              notes: notes,
              labelsToUpdate: labelsToUpdate,
              putSuggestions: putSuggestions,
              hasCPTBeenUpdated: hasCPTBeenUpdated
            })
          : undefined
      ]);

      if (
        !loading &&
        !error &&
        (isTimeSignalFederatedSuggestionsEnabled
          ? !putSuggestionsLoading && !putSuggestionsError
          : true)
      ) {
        setToastContext({
          showSuccess: true,
          message: 'note.multiEdit.nNotesUpdated',
          values: {
            count: `${notes.length}`
          }
        });
      } else if (
        error ||
        (isTimeSignalFederatedSuggestionsEnabled ? putSuggestionsError : false)
      ) {
        setToastContext({
          showError: true,
          message: 'toast.note.failure'
        });
      }
    },
    [
      userUri,
      updateTimeSignalsById,
      isTimeSignalFederatedSuggestionsEnabled,
      putSuggestions,
      loading,
      error,
      putSuggestionsLoading,
      putSuggestionsError,
      isSmartSuggestionEnabled,
      isEmptyLabelsEnabled,
      setToastContext
    ]
  );

  return {
    onMultiEditTimeSignals,
    updateInProgress:
      loading ||
      (isTimeSignalFederatedSuggestionsEnabled ? putSuggestionsLoading : false)
  };
};
